import { useQuery } from "@tanstack/react-query";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import type { SoapCallStatus, SoapOperation } from "@/lib/enums";
import { soapCallLogKeys } from "@/lib/query";

/**
 * TanStack Query hooks for `soap_api_call_log` (admin-only). `query.status`
 * maps directly onto the endpoint's own `status` param (success/error) — no
 * repurposing needed, unlike the audit log view which reuses the grid's
 * generic slot for a different field. `operation`/`buInstanceId` are this
 * grid's own filters, appended onto `buildListParams`'s output (same pattern
 * as `useMasterDataMappings`'s `BuPairFilter`).
 */

export interface SoapCallLogEntry {
  id: string;
  operation: SoapOperation;
  buInstanceId: string | null;
  buInstanceName: string | null;
  endpointUrl: string;
  httpStatus: number | null;
  status: SoapCallStatus;
  errorMessage: string | null;
  durationMs: number;
  initiatorId: string | null;
  initiatorEmail: string | null;
  createdAt: string;
}

export interface SoapCallLogDetail extends SoapCallLogEntry {
  requestHeaders: Record<string, string> | null;
  requestBody: string;
  responseBody: string | null;
}

interface SoapCallLogListResponse {
  data: SoapCallLogEntry[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface SoapCallLogExtraFilters {
  operation?: string;
  buInstanceId?: string;
}

async function extractErrorMessage(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  return json?.error ?? fallback;
}

export function useSoapCallLog(
  query: DataGridQuery,
  pageRequest: PageRequest,
  extraFilters: SoapCallLogExtraFilters = {},
) {
  return useQuery({
    queryKey: [...soapCallLogKeys.list({ ...query, ...extraFilters }), pageRequest] as const,
    queryFn: async (): Promise<SoapCallLogListResponse> => {
      const params = buildListParams(query, pageRequest);
      if (extraFilters.operation) params.set("operation", extraFilters.operation);
      if (extraFilters.buInstanceId) params.set("buInstanceId", extraFilters.buInstanceId);

      const res = await fetch(`/api/admin/soap-call-log?${params}`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch SOAP call log"));
      return res.json();
    },
  });
}

/** Full row (including request/response XML) for the detail sheet — fetched only when opened. */
export function useSoapCallLogDetail(id: string | null) {
  return useQuery({
    queryKey: soapCallLogKeys.detail(id ?? ""),
    queryFn: async (): Promise<SoapCallLogDetail> => {
      const res = await fetch(`/api/admin/soap-call-log/${id}`);
      if (!res.ok) throw new Error(await extractErrorMessage(res, "Failed to fetch call detail"));
      return res.json();
    },
    enabled: id !== null,
  });
}
